import React, { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { useLayout } from "../../contexts/LayoutContext";
import {
  resolveFeatureSections,
  shouldShowProviderRole,
} from "../../utils/featureSections";
import {
  completePayPalPayoutLink,
  createProviderConsultation,
  createProviderCourse,
  createProviderJob,
  createIbanWithdrawalRequest,
  createProviderService,
  createSellerProduct,
  createSellerProductVariant,
  createStripeDashboardLink,
  createStripeConnectLink,
  createUserBankAccount,
  createProviderPayoutMethod,
  createProviderWithdrawalRequest,
  deleteUserBankAccount,
  deleteProviderConsultation,
  deleteProviderCourse,
  deleteProviderJob,
  deleteProviderService,
  deleteSellerProduct,
  deleteSellerProductVariant,
  deleteProviderPayoutMethod,
  fetchPayPalPayoutLinkConfig,
  fetchProviderConsultations,
  fetchProviderCourses,
  fetchProviderJobs,
  fetchStripeConnectStatus,
  fetchProviderDashboard,
  fetchProviderEarnings,
  fetchProviderServices,
  fetchSellerProductMetadata,
  fetchSellerProducts,
  setDefaultUserBankAccount,
  submitProviderApplication,
  updateProviderConsultation,
  updateProviderCourse,
  updateProviderJob,
  updateProviderService,
  updateUserBankAccount,
  updateSellerProduct,
  updateSellerProductVariant,
  updateProviderPayoutMethod,
} from "../../api/providerCenter";
import { firstSupportedMethod, getCountryPayoutAvailability } from "../../utils/methodAvailability";

import {
  LINKED_PAYPAL_PAYOUT_REASON,
  createEmptyConsultationForm,
  createEmptyCourseForm,
  createEmptyJobForm,
  createEmptyServiceForm,
  createEmptyForm,
  createEmptySellerProductForm,
  createEmptySellerVariantForm,
  createEmptyPayoutMethodForm,
  createEmptyWithdrawalForm,
  createEmptyBankAccountForm,
  createEmptyPayPalLinkConfig,
  getStatusLabel,
  getStatusBadgeClass,
  getContentPendingCount,
  getDashboardSectionId,
  formatMoney,
  getPayoutMethodLabel,
} from "./utils";

import ApplicationPanel from "./ApplicationPanel";
import PayoutMethodsPanel from "./PayoutMethodsPanel";
import EarningsSummary from "./EarningsSummary";
import WithdrawalPanel from "./WithdrawalPanel";
import BankAccountsPanel from "./BankAccountsPanel";
import SavedMethodsList from "./SavedMethodsList";
import WithdrawalHistory from "./WithdrawalHistory";
import SellerProductsPanel from "./SellerProductsPanel";
import ContentStatusTable from "./ContentStatusTable";
import ProviderActivityCard from "./ProviderActivityCard";
import ServicesManagerPanel from "./ServicesManagerPanel";
import ConsultationsManagerPanel from "./ConsultationsManagerPanel";
import CoursesManagerPanel from "./CoursesManagerPanel";
import JobsManagerPanel from "./JobsManagerPanel";

let payPalPayoutScriptPromise = null;

function isAccessGatingError(error) {
  return [403, 423].includes(Number(error?.status || 0));
}

async function resolveOptionalProviderRequest(request, fallbackValue) {
  try {
    return await request();
  } catch (error) {
    if (isAccessGatingError(error)) {
      return fallbackValue;
    }

    throw error;
  }
}

function WorkspaceSection({ id, eyebrow, title, description, stats = [], actions = [], children }) {
  return (
    <section className="provider-center__workspace-section mb-4" id={id}>
      <div className="provider-center__workspace-header">
        <div className="provider-center__workspace-copy">
          {eyebrow ? <div className="provider-center__workspace-eyebrow">{eyebrow}</div> : null}
          <h4 className="provider-center__workspace-title">{title}</h4>
          {description ? <p className="provider-center__workspace-description mb-0">{description}</p> : null}
        </div>

        {stats.length > 0 ? (
          <div className="provider-center__workspace-stats">
            {stats.map((item) => (
              <div className="provider-center__workspace-stat" key={item.label}>
                <div className="provider-center__workspace-stat-value">{item.value}</div>
                <div className="provider-center__workspace-stat-label">{item.label}</div>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {actions.length > 0 ? (
        <div className="provider-center__workspace-actions">
          {actions.map((action) =>
            action.to ? (
              <Link
                key={`${action.label}-${action.to}`}
                to={action.to}
                className={`btn rounded-pill ${action.dark ? "btn-dark" : "btn-outline-dark"}`}
              >
                {action.label}
              </Link>
            ) : (
              <a
                key={`${action.label}-${action.href}`}
                href={action.href}
                className={`btn rounded-pill ${action.dark ? "btn-dark" : "btn-outline-dark"}`}
              >
                {action.label}
              </a>
            ),
          )}
        </div>
      ) : null}

      <div className="provider-center__workspace-body">{children}</div>
    </section>
  );
}

function loadPayPalPayoutScript(clientId) {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("PayPal linking is only available in the browser."));
  }
  if (window.paypal?.PayoutsAAC) {
    return Promise.resolve(window.paypal);
  }
  if (!payPalPayoutScriptPromise) {
    payPalPayoutScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://www.paypalobjects.com/payouts/js/payouts_aac.js";
      script.async = true;
      script.defer = true;

      let timeoutId = setTimeout(() => {
        console.error("PayPal script timeout - script did not load within 15 seconds");
        reject(new Error("Failed to load the PayPal payout linking script (timeout). Server may be unreachable."));
      }, 15000);

      script.onload = () => {
        clearTimeout(timeoutId);
        if (window.paypal?.PayoutsAAC) {
          resolve(window.paypal);
        } else {
          console.error("✗ PayoutsAAC not found in window.paypal after script load");
          reject(new Error("PayPal script loaded but PayoutsAAC is not available. Please refresh the page."));
        }
      };

      script.onerror = (error) => {
        clearTimeout(timeoutId);
        console.error("PayPal payout script load failed:", error);
        reject(new Error("Failed to load the PayPal payout linking script. Check your internet connection or try again later."));
      };

      document.head.appendChild(script);
    });
  }
  return payPalPayoutScriptPromise;
}

function containerHasPayPalButton(containerId) {
  if (typeof document === "undefined") return false;
  const container = document.getElementById(containerId);
  return Boolean(container && container.childElementCount > 0);
}

function isAdminAccount(user) {
  if (!user || typeof user !== "object") return false;

  const role = String(user.role || "").trim().toLowerCase();
  const isAdminFlag = String(user.is_admin ?? "").trim().toLowerCase();
  const userId = Number(user.id || 0);

  return (
    role === "admin" ||
    isAdminFlag === "1" ||
    isAdminFlag === "true" ||
    isAdminFlag === "yes" ||
    userId === 1
  );
}

export default function ProviderCenter({ embedded = false, activeTab = "overview", setActiveTab = null, onDataLoad = null }) {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const { settings } = useLayout();
  const location = useLocation();
  const isAr = lang === "ar";
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isAdmin = useMemo(() => isAdminAccount(user), [user]);

  const [dashboard, setDashboard] = useState({
    profiles: [],
    content: { courses: [], consultations: [], services: [], jobs: [], products: [] },
    summary: {},
  });
  const [earnings, setEarnings] = useState({
    summary: {},
    methods: [],
    bankAccounts: [],
    ibanSettings: {
      globalEnabled: false,
      firstManualReviewCount: 5,
      autoProcessAfterThreshold: true,
      feeRate: 0.0003,
      userEnabled: true,
      forceManualReview: false,
      manualReviewThreshold: null,
      blockedReason: "",
    },
    requests: [],
    countryMethodAvailability: {
      country_code: "",
      country_known: false,
      payouts: {
        types: {
          legacy: { supported: true, reason: null },
          iban_wise: { supported: true, reason: null },
        },
        legacy_methods: {
          paypal: { supported: false, reason: LINKED_PAYPAL_PAYOUT_REASON },
          test_paypal: { supported: false, reason: "PayPal sandbox mode is admin-only." },
          stripe: { supported: true, reason: null },
        },
      },
    },
  });
  const [form, setForm] = useState(() => createEmptyForm());
  const [sellerForm, setSellerForm] = useState(() => createEmptySellerProductForm());
  const [sellerVariantForm, setSellerVariantForm] = useState(() => createEmptySellerVariantForm());
  const [editingSellerVariantsProductId, setEditingSellerVariantsProductId] = useState(0);
  const [sellerMeta, setSellerMeta] = useState({
    enabled: true,
    categories: [],
    attributes: [],
    attributeValues: [],
  });
  const [sellerProducts, setSellerProducts] = useState([]);
  const [providerServices, setProviderServices] = useState([]);
  const [providerConsultations, setProviderConsultations] = useState([]);
  const [providerCourses, setProviderCourses] = useState([]);
  const [providerJobs, setProviderJobs] = useState([]);
  const [serviceForm, setServiceForm] = useState(() => createEmptyServiceForm());
  const [consultationForm, setConsultationForm] = useState(() => createEmptyConsultationForm());
  const [courseForm, setCourseForm] = useState(() => createEmptyCourseForm());
  const [jobForm, setJobForm] = useState(() => createEmptyJobForm());
  const [methodForm, setMethodForm] = useState(() => createEmptyPayoutMethodForm());
  const [bankAccountForm, setBankAccountForm] = useState(() => createEmptyBankAccountForm());
  const [withdrawalForm, setWithdrawalForm] = useState(() => createEmptyWithdrawalForm());
  const [paypalLinkConfigs, setPaypalLinkConfigs] = useState({
    paypal: createEmptyPayPalLinkConfig("paypal"),
    test_paypal: createEmptyPayPalLinkConfig("test_paypal"),
  });
  const [payPalButtonStates, setPayPalButtonStates] = useState({
    paypal: "idle",
    test_paypal: "idle",
  });
  const [payPalButtonRetryKey, setPayPalButtonRetryKey] = useState(0);
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSellerProduct, setSavingSellerProduct] = useState(false);
  const [savingSellerVariant, setSavingSellerVariant] = useState(false);
  const [savingService, setSavingService] = useState(false);
  const [savingConsultation, setSavingConsultation] = useState(false);
  const [savingCourse, setSavingCourse] = useState(false);
  const [savingJob, setSavingJob] = useState(false);
  const [savingMethod, setSavingMethod] = useState(false);
  const [savingBankAccount, setSavingBankAccount] = useState(false);
  const [savingWithdrawal, setSavingWithdrawal] = useState(false);

  const sections = useMemo(
    () => resolveFeatureSections(settings, { sellerMarketplace: sellerMeta.enabled }),
    [sellerMeta.enabled, settings],
  );
  const effectiveSections = useMemo(
    () =>
      isAdmin
        ? {
            ...sections,
            courses: true,
            consultations: true,
            services: true,
            jobs: true,
            sellerMarketplace: true,
          }
        : sections,
    [isAdmin, sections],
  );
  const sellerMarketplaceEnabled = effectiveSections.sellerMarketplace;
  const sellerOptionAttributes = useMemo(
    () =>
      Array.isArray(sellerMeta.attributes)
        ? sellerMeta.attributes.filter((attribute) => attribute.slug !== "default")
        : [],
    [sellerMeta.attributes],
  );
  const activeSellerVariantProduct = useMemo(
    () => sellerProducts.find((product) => product.id === editingSellerVariantsProductId) || null,
    [editingSellerVariantsProductId, sellerProducts],
  );

  useEffect(() => {
    let cancelled = false;
    async function loadAll() {
      try {
        setLoading(true);
        const [
          providerData,
          earningsData,
          sellerMetadata,
          sellerProductsData,
          providerServicesData,
          providerConsultationsData,
          providerCoursesData,
          providerJobsData,
          payPalConfig,
          testPayPalConfig,
        ] = await Promise.all([
          fetchProviderDashboard(),
          fetchProviderEarnings(),
          resolveOptionalProviderRequest(fetchSellerProductMetadata, { categories: [], attributes: [], attributeValues: [] }),
          resolveOptionalProviderRequest(fetchSellerProducts, []),
          resolveOptionalProviderRequest(fetchProviderServices, []),
          resolveOptionalProviderRequest(fetchProviderConsultations, []),
          resolveOptionalProviderRequest(fetchProviderCourses, []),
          resolveOptionalProviderRequest(fetchProviderJobs, []),
          fetchPayPalPayoutLinkConfig("paypal"),
          fetchPayPalPayoutLinkConfig("test_paypal"),
        ]);
        if (!cancelled) {
          setDashboard(providerData);
          setEarnings(earningsData);
          if (onDataLoad) {
            onDataLoad({ dashboard: providerData, earnings: earningsData });
          }
          setSellerMeta(sellerMetadata);
          setSellerProducts(sellerProductsData);
          setProviderServices(providerServicesData);
          setProviderConsultations(providerConsultationsData);
          setProviderCourses(providerCoursesData);
          setProviderJobs(providerJobsData);
          setPaypalLinkConfigs({ paypal: payPalConfig, test_paypal: testPayPalConfig });
          if (Array.isArray(earningsData.methods) && earningsData.methods.length > 0) {
            const defaultMethod =
              earningsData.methods.find((item) => item.isDefault) || earningsData.methods[0];
            setWithdrawalForm((current) => ({
              ...current,
              payoutMethodId: defaultMethod?.id ? String(defaultMethod.id) : current.payoutMethodId,
            }));
          }
          if (Array.isArray(earningsData.bankAccounts) && earningsData.bankAccounts.length > 0) {
            const defaultBankAccount =
              earningsData.bankAccounts.find((item) => item.isDefault) ||
              earningsData.bankAccounts[0];
            setWithdrawalForm((current) => ({
              ...current,
              bankAccountId: defaultBankAccount?.id
                ? String(defaultBankAccount.id)
                : current.bankAccountId,
            }));
          }
        }
      } catch (error) {
        if (!cancelled) {
          setNotice({
            type: "danger",
            message:
              error?.body?.message ||
              error?.message ||
              (isAr ? "تعذر تحميل مركز مقدم الخدمة." : "Failed to load the provider center."),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadAll();
    return () => { cancelled = true; };
  }, [isAr]);

  useEffect(() => {
    if (embedded && activeTab !== "overview") {
      const workspaceStack = document.querySelector(".provider-center__workspace-stack");
      if (workspaceStack) {
        workspaceStack.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [activeTab, embedded]);

  const currentRoleProfile = useMemo(
    () => dashboard.profiles.find((p) => p.providerRole === form.providerRole) || null,
    [dashboard.profiles, form.providerRole],
  );
  const sellerProfile = useMemo(
    () => dashboard.profiles.find((p) => p.providerRole === "seller") || null,
    [dashboard.profiles],
  );
  const freelancerProfile = useMemo(
    () => dashboard.profiles.find((p) => p.providerRole === "freelancer") || null,
    [dashboard.profiles],
  );
  const consultantProfile = useMemo(
    () => dashboard.profiles.find((p) => p.providerRole === "consultant") || null,
    [dashboard.profiles],
  );
  const trainerProfile = useMemo(
    () => dashboard.profiles.find((p) => p.providerRole === "trainer") || null,
    [dashboard.profiles],
  );
  const canManageSellerProducts =
    isAdmin || (sellerMarketplaceEnabled && sellerProfile && sellerProfile.status === "approved");
  const profileByRole = useMemo(
    () =>
      dashboard.profiles.reduce((acc, p) => { acc[p.providerRole] = p; return acc; }, {}),
    [dashboard.profiles],
  );
  const visibleProviderRoles = useMemo(
    () =>
      ["trainer", "consultant", "freelancer", "employer", "seller"].filter((role) =>
        shouldShowProviderRole(role, effectiveSections, dashboard.profiles, form.providerRole),
      ),
    [dashboard.profiles, effectiveSections, form.providerRole],
  );
  const disabledRoleMessages = {
    trainer: isAr
      ? "قسم الكورسات معطل حاليًا من الإدارة، لذلك لا يمكن إرسال طلب مدرب جديد الآن."
      : "The courses section is currently disabled by the admin, so new trainer applications are paused right now.",
    consultant: isAr
      ? "قسم الاستشارات معطل حاليًا من الإدارة، لذلك لا يمكن إرسال طلب مستشار جديد الآن."
      : "The consultations section is currently disabled by the admin, so new consultant applications are paused right now.",
    freelancer: isAr
      ? "قسم الخدمات معطل حاليًا من الإدارة، لذلك لا يمكن إرسال طلب مقدم خدمة جديد الآن."
      : "The freelancer services section is currently disabled by the admin, so new service-provider applications are paused right now.",
    employer: isAr
      ? "قسم الوظائف معطل حاليًا من الإدارة، لذلك لا يمكن إرسال طلب صاحب وظائف جديد الآن."
      : "The jobs section is currently disabled by the admin, so new employer applications are paused right now.",
    seller: isAr
      ? "قسم البائعين معطل حاليًا من الإدارة، لذلك لا يمكن إرسال طلب بائع جديد الآن."
      : "The seller section is currently disabled by the admin, so new seller applications are paused right now.",
  };
  const isCurrentRoleEnabled =
    form.providerRole === "seller"
      ? sellerMarketplaceEnabled
      : form.providerRole === "trainer"
        ? effectiveSections.courses
        : form.providerRole === "consultant"
          ? effectiveSections.consultations
          : form.providerRole === "freelancer"
            ? effectiveSections.services
            : form.providerRole === "employer"
              ? effectiveSections.jobs
              : true;

  useEffect(() => {
    if (visibleProviderRoles.length === 0) return;
    if (!visibleProviderRoles.includes(form.providerRole)) {
      setForm((current) => ({ ...current, providerRole: visibleProviderRoles[0] }));
    }
  }, [form.providerRole, visibleProviderRoles]);

  const selectedBankAccount = useMemo(
    () =>
      earnings.bankAccounts.find(
        (account) => String(account.id) === String(withdrawalForm.bankAccountId),
      ) || null,
    [earnings.bankAccounts, withdrawalForm.bankAccountId],
  );
  const activeLegacyMethods = useMemo(
    () => earnings.methods.filter((method) => method.status === "active"),
    [earnings.methods],
  );
  const stripeLinkedMethod = useMemo(
    () => earnings.methods.find((method) => method.method === "stripe") || null,
    [earnings.methods],
  );
  const paypalLinkedMethod = useMemo(
    () => earnings.methods.find((method) => method.method === "paypal") || null,
    [earnings.methods],
  );
  const testPayPalLinkedMethod = useMemo(
    () => earnings.methods.find((method) => method.method === "test_paypal") || null,
    [earnings.methods],
  );
  const payPalLinkConfig = paypalLinkConfigs.paypal || createEmptyPayPalLinkConfig("paypal");
  const testPayPalLinkConfig =
    paypalLinkConfigs.test_paypal || createEmptyPayPalLinkConfig("test_paypal");
  const payPalClientId = payPalLinkConfig.clientIds?.[payPalLinkConfig.env] || "";
  const testPayPalClientId = testPayPalLinkConfig.clientIds?.[testPayPalLinkConfig.env] || "";
  const supportedLegacyMethodOptions = useMemo(() => {
    const options = [
      {
        code: "paypal",
        label: getPayoutMethodLabel("paypal", isAr),
        availability: getCountryPayoutAvailability(earnings, "payouts.legacy_methods.paypal"),
      },
      {
        code: "test_paypal",
        label: getPayoutMethodLabel("test_paypal", isAr),
        availability: getCountryPayoutAvailability(earnings, "payouts.legacy_methods.test_paypal"),
      },
      {
        code: "stripe",
        label: getPayoutMethodLabel("stripe", isAr),
        availability: getCountryPayoutAvailability(earnings, "payouts.legacy_methods.stripe"),
      },
    ];
    return options.filter((option) => option.availability?.supported);
  }, [earnings, isAr]);
  const activeLegacyMethodLabels = useMemo(
    () =>
      activeLegacyMethods.map((method) => {
        const label = getPayoutMethodLabel(method.method, isAr);
        return method.accountLabel ? `${label} - ${method.accountLabel}` : label;
      }),
    [activeLegacyMethods, isAr],
  );
  const ibanWithdrawalEnabled =
    Boolean(earnings.ibanSettings?.globalEnabled) && Boolean(earnings.ibanSettings?.userEnabled);
  const legacyWithdrawalAvailability = getCountryPayoutAvailability(earnings, "payouts.types.legacy");
  const ibanWithdrawalTypeAvailability = getCountryPayoutAvailability(
    earnings,
    "payouts.types.iban_wise",
  );
  const ibanFeeRate = Number(earnings.ibanSettings?.feeRate || 0.0003);
  const withdrawalAmountValue = Number(withdrawalForm.amount || 0);
  const ibanPlatformFee = useMemo(
    () => Math.round(withdrawalAmountValue * ibanFeeRate * 100) / 100,
    [withdrawalAmountValue, ibanFeeRate],
  );
  const ibanNetAmount = useMemo(
    () => Math.max(0, Math.round((withdrawalAmountValue - ibanPlatformFee) * 100) / 100),
    [withdrawalAmountValue, ibanPlatformFee],
  );

  const dashboardSections = useMemo(
    () =>
      [
        {
        key: "seller",
        title: isAr ? "البائع" : "Seller",
        subtitle: isAr ? "المنتجات والمتجر" : "Products and storefront",
        total: sellerProducts.length,
        pending: sellerProducts.filter((p) => p.approvalStatus === "pending").length,
        statusLabel: isAdmin
          ? isAr ? "أدمن" : "Admin"
          : sellerProfile
          ? getStatusLabel(sellerProfile.status, isAr)
          : isAr ? "غير مرسل" : "Not applied",
        statusClassName: isAdmin
          ? "bg-success-subtle text-success"
          : sellerProfile
          ? getStatusBadgeClass(sellerProfile.status)
          : "bg-secondary-subtle text-secondary",
        href: `#${getDashboardSectionId("seller")}`,
        ctaLabel: canManageSellerProducts
          ? isAr ? "إدارة المنتجات" : "Manage products"
          : isAr ? "فتح قسم البائع" : "Open seller area",
        accentClassName: "text-warning",
        icon: "bi-bag-check",
        },
        {
        key: "services",
        title: isAr ? "الخدمات" : "Services",
        subtitle: isAr ? "الخدمات المستقلة والطلبات" : "Freelance services and orders",
        total: dashboard.content.services.length,
        pending: getContentPendingCount(dashboard.content.services),
        statusLabel: isAdmin
          ? isAr ? "أدمن" : "Admin"
          : profileByRole.freelancer
          ? getStatusLabel(profileByRole.freelancer.status, isAr)
          : isAr ? "غير مرسل" : "Not applied",
        statusClassName: isAdmin
          ? "bg-success-subtle text-success"
          : profileByRole.freelancer
          ? getStatusBadgeClass(profileByRole.freelancer.status)
          : "bg-secondary-subtle text-secondary",
        href: `#${getDashboardSectionId("services")}`,
        ctaLabel: isAr ? "متابعة الخدمات" : "View services",
        accentClassName: "text-primary",
        icon: "bi-briefcase",
        },
        {
        key: "consultations",
        title: isAr ? "الاستشارات" : "Consultations",
        subtitle: isAr ? "جلساتك وحجوزاتك" : "Sessions and bookings",
        total: dashboard.content.consultations.length,
        pending: getContentPendingCount(dashboard.content.consultations),
        statusLabel: isAdmin
          ? isAr ? "أدمن" : "Admin"
          : profileByRole.consultant
          ? getStatusLabel(profileByRole.consultant.status, isAr)
          : isAr ? "غير مرسل" : "Not applied",
        statusClassName: isAdmin
          ? "bg-success-subtle text-success"
          : profileByRole.consultant
          ? getStatusBadgeClass(profileByRole.consultant.status)
          : "bg-secondary-subtle text-secondary",
        href: `#${getDashboardSectionId("consultations")}`,
        ctaLabel: isAr ? "متابعة الاستشارات" : "View consultations",
        accentClassName: "text-info",
        icon: "bi-chat-square-dots",
        },
        {
        key: "courses",
        title: isAr ? "الكورسات" : "Courses",
        subtitle: isAr ? "المحتوى والتسجيلات" : "Content and enrollments",
        total: dashboard.content.courses.length,
        pending: getContentPendingCount(dashboard.content.courses),
        statusLabel: isAdmin
          ? isAr ? "أدمن" : "Admin"
          : profileByRole.trainer
          ? getStatusLabel(profileByRole.trainer.status, isAr)
          : isAr ? "غير مرسل" : "Not applied",
        statusClassName: isAdmin
          ? "bg-success-subtle text-success"
          : profileByRole.trainer
          ? getStatusBadgeClass(profileByRole.trainer.status)
          : "bg-secondary-subtle text-secondary",
        href: `#${getDashboardSectionId("courses")}`,
        ctaLabel: isAr ? "متابعة الكورسات" : "View courses",
        accentClassName: "text-success",
        icon: "bi-easel2",
        },
        effectiveSections.jobs
          ? {
              key: "jobs",
              title: isAr ? "الوظائف" : "Jobs",
              subtitle: isAr ? "إعلانات الوظائف والتوظيف" : "Listings and hiring posts",
              total: dashboard.content.jobs.length,
              pending: getContentPendingCount(dashboard.content.jobs),
              statusLabel: isAdmin
                ? isAr ? "أدمن" : "Admin"
                : profileByRole.employer
                ? getStatusLabel(profileByRole.employer.status, isAr)
                : isAr ? "غير مرسل" : "Not applied",
              statusClassName: isAdmin
                ? "bg-success-subtle text-success"
                : profileByRole.employer
                ? getStatusBadgeClass(profileByRole.employer.status)
                : "bg-secondary-subtle text-secondary",
              href: `#${getDashboardSectionId("jobs")}`,
              ctaLabel: isAr ? "متابعة الوظائف" : "View jobs",
              accentClassName: "text-danger",
              icon: "bi-building",
            }
          : null,
      ].filter(Boolean),
    [
      canManageSellerProducts,
      dashboard.content.consultations,
      dashboard.content.courses,
      dashboard.content.jobs,
      dashboard.content.services,
      effectiveSections.jobs,
      isAdmin,
      isAr,
      profileByRole,
      sellerProducts,
      sellerProfile,
    ],
  );

  const dashboardHighlights = [
    {
      key: "approvedRoles",
      icon: "bi-patch-check",
      label: isAr ? "الأدوار المعتمدة" : "Approved roles",
      value: dashboard.profiles.filter((p) => p.status === "approved").length,
    },
    {
      key: "pendingContent",
      icon: "bi-hourglass-split",
      label: isAr ? "محتوى قيد المراجعة" : "Pending content",
      value:
        getContentPendingCount(dashboard.content.courses) +
        getContentPendingCount(dashboard.content.consultations) +
        getContentPendingCount(dashboard.content.services) +
        getContentPendingCount(dashboard.content.jobs) +
        getContentPendingCount(dashboard.content.products),
    },
    {
      key: "availableBalance",
      icon: "bi-wallet2",
      label: isAr ? "المتاح للسحب" : "Available balance",
      value: formatMoney(earnings.summary.available_balance, earnings.summary.currency),
      emphasisClassName: "text-success",
    },
    {
      key: "totalListings",
      icon: "bi-grid-3x3-gap",
      label: isAr ? "إجمالي العناصر" : "Total listings",
      value:
        dashboard.content.courses.length +
        dashboard.content.consultations.length +
        dashboard.content.services.length +
        dashboard.content.jobs.length +
        dashboard.content.products.length,
    },
  ];

  const dashboardQuickActions = useMemo(
    () =>
      [
        { key: "application", href: `#${getDashboardSectionId("application")}`, label: isAr ? "الاعتماد" : "Applications", dark: true },
        { key: "payouts", href: `#${getDashboardSectionId("payouts")}`, label: isAr ? "السحب والأرباح" : "Payouts & earnings" },
        { key: "seller", href: `#${getDashboardSectionId("seller")}`, label: isAr ? "المنتجات" : "Products" },
        { key: "services", href: `#${getDashboardSectionId("services")}`, label: isAr ? "الخدمات" : "Services" },
        { key: "consultations", href: `#${getDashboardSectionId("consultations")}`, label: isAr ? "الاستشارات" : "Consultations" },
        { key: "courses", href: `#${getDashboardSectionId("courses")}`, label: isAr ? "الكورسات" : "Courses" },
        effectiveSections.jobs
          ? { key: "jobs", href: `#${getDashboardSectionId("jobs")}`, label: isAr ? "الوظائف" : "Jobs" }
          : null,
      ].filter(Boolean),
    [effectiveSections.jobs, isAr],
  );

  const enteredBankCountryCode = useMemo(() => {
    const normalized = String(bankAccountForm.iban || "").replace(/\s+/g, "").toUpperCase();
    return normalized.length >= 2 ? normalized.slice(0, 2) : "";
  }, [bankAccountForm.iban]);

  useEffect(() => {
    if (methodForm.id) return;
    const nextMethod = firstSupportedMethod(supportedLegacyMethodOptions, "");
    if (nextMethod !== methodForm.method) {
      setMethodForm((current) => ({ ...current, method: nextMethod }));
    }
  }, [methodForm.id, methodForm.method, supportedLegacyMethodOptions]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stripeConnectState = params.get("stripe_connect");
    if (!stripeConnectState) return;
    let cancelled = false;
    async function refreshStripeStatus() {
      try {
        const result = await fetchStripeConnectStatus();
        if (cancelled) return;
        const refreshedEarnings = await fetchProviderEarnings();
        if (cancelled) return;
        setEarnings(refreshedEarnings);
        setNotice({
          type: result.method?.meta?.onboarding_complete ? "success" : "info",
          message:
            stripeConnectState === "return"
              ? isAr
                ? "تم تحديث حالة ربط Stripe. راجع حالة الحساب قبل طلب السحب."
                : "Stripe linking status has been updated. Review the account status before requesting a withdrawal."
              : isAr
                ? "تم تحديث حالة ربط Stripe."
                : "Stripe linking status has been refreshed.",
        });
      } catch (error) {
        if (!cancelled) {
          setNotice({
            type: "danger",
            message:
              error?.body?.message ||
              error?.message ||
              (isAr ? "تعذر تحديث حالة Stripe." : "Failed to refresh Stripe status."),
          });
        }
      }
    }
    refreshStripeStatus();
    return () => { cancelled = true; };
  }, [isAr, location.search]);

  useEffect(() => {
    const configs = [
      {
        method: "paypal",
        label: "PayPal",
        config: payPalLinkConfig,
        clientId: payPalClientId,
        linkedMethod: paypalLinkedMethod,
      },
      {
        method: "test_paypal",
        label: isAr ? "PayPal التجريبي" : "PayPal Test",
        config: testPayPalLinkConfig,
        clientId: testPayPalClientId,
        linkedMethod: testPayPalLinkedMethod,
      },
    ];
    let cancelled = false;
    async function renderPayPalButtons() {
      for (const entry of configs) {
        const { method, label, config, clientId, linkedMethod } = entry;
        const containerId = `paypal-payout-link-button-${method}`;
        if (!config.enabled || !config.merchantId || !clientId) {
          setPayPalButtonStates((current) => ({ ...current, [method]: "disabled" }));
          const container = document.getElementById(containerId);
          if (container) container.innerHTML = "";
          continue;
        }
        try {
          setPayPalButtonStates((current) => ({ ...current, [method]: "loading" }));
          await loadPayPalPayoutScript(clientId);
          if (cancelled || !window.paypal?.PayoutsAAC) {
            if (!cancelled) setPayPalButtonStates((current) => ({ ...current, [method]: "error" }));
            continue;
          }
          const container = document.getElementById(containerId);
          if (!container) {
            if (!cancelled) setPayPalButtonStates((current) => ({ ...current, [method]: "error" }));
            continue;
          }
          container.innerHTML = "";
          console.log(`PayPal ${method} config:`, {
            env: config.env,
            clientIds: config.clientIds,
            merchantId: config.merchantId,
            enabled: config.enabled,
            reason: config.reason,
          });
          window.paypal.PayoutsAAC.render(
            {
              env: config.env,
              clientId: config.clientIds,
              merchantId: config.merchantId,
              pageType: linkedMethod ? "login" : "signup",
              onLogin: async (data) => {
                const code = data?.body?.code || "";
                const errorMessage = data?.body?.message || data?.message || "";
                if (!code) {
                  setNotice({
                    type: "danger",
                    message: errorMessage || (isAr ? `تعذر إكمال ربط ${label}.` : `Unable to complete ${label} linking.`),
                  });
                  return;
                }
                try {
                  setSavingMethod(true);
                  const result = await completePayPalPayoutLink(code, method);
                  const refreshedEarnings = await fetchProviderEarnings();
                  if (cancelled) return;
                  setEarnings(refreshedEarnings);
                  setWithdrawalForm((current) => ({
                    ...current,
                    methodType: "legacy",
                    payoutMethodId: result.method?.id ? String(result.method.id) : current.payoutMethodId,
                  }));
                  setNotice({ type: "success", message: result.message });
                } catch (error) {
                  if (!cancelled) {
                    setNotice({
                      type: "danger",
                      message: error?.body?.message || error?.message || (isAr ? `فشل ربط ${label}.` : `Failed to link the ${label} payout account.`),
                    });
                  }
                } finally {
                  if (!cancelled) setSavingMethod(false);
                }
              },
              onError: (error) => {
                if (!cancelled) {
                  setNotice({
                    type: "danger",
                    message: error?.message || (isAr ? `فشل تحميل ${label}.` : `Failed to load ${label} payout linking.`),
                  });
                }
              },
            },
            `#${containerId}`,
          );
          window.setTimeout(() => {
            if (!cancelled) {
              setPayPalButtonStates((current) => ({
                ...current,
                [method]: containerHasPayPalButton(containerId) ? "rendered" : "error",
              }));
            }
          }, 1500);
        } catch (error) {
          if (!cancelled) {
            setPayPalButtonStates((current) => ({ ...current, [method]: "error" }));
            setNotice({
              type: "danger",
              message: error?.message || (isAr ? `فشل تحميل ${label}.` : `Failed to load ${label} payout linking.`),
            });
          }
        }
      }
    }
    renderPayPalButtons();
    return () => {
      cancelled = true;
      for (const method of ["paypal", "test_paypal"]) {
        const container = document.getElementById(`paypal-payout-link-button-${method}`);
        if (container) container.innerHTML = "";
      }
    };
  }, [
    isAr,
    payPalButtonRetryKey,
    payPalClientId,
    payPalLinkConfig,
    paypalLinkedMethod,
    testPayPalClientId,
    testPayPalLinkConfig,
    testPayPalLinkedMethod,
  ]);

  useEffect(() => {
    const supportedTypes = [
      legacyWithdrawalAvailability?.supported ? "legacy" : null,
      ibanWithdrawalTypeAvailability?.supported ? "iban_wise" : null,
    ].filter(Boolean);
    if (supportedTypes.includes(withdrawalForm.methodType)) return;
    const nextType = supportedTypes[0];
    if (nextType) setWithdrawalForm((current) => ({ ...current, methodType: nextType }));
  }, [ibanWithdrawalTypeAvailability, legacyWithdrawalAvailability, withdrawalForm.methodType]);

  // --- Field updaters ---
  function updateField(field, value) { setForm((current) => ({ ...current, [field]: value })); }
  function updateServiceField(field, value) {
    setServiceForm((current) => ({ ...current, [field]: value }));
  }
  function updateConsultationField(field, value) {
    setConsultationForm((current) => ({ ...current, [field]: value }));
  }
  function updateCourseField(field, value) {
    setCourseForm((current) => ({ ...current, [field]: value }));
  }
  function updateJobField(field, value) {
    setJobForm((current) => ({ ...current, [field]: value }));
  }
  function updateSellerField(field, value) { setSellerForm((current) => ({ ...current, [field]: value })); }
  function updateSellerVariantField(field, value) {
    setSellerVariantForm((current) => ({ ...current, [field]: value }));
  }
  function updateMethodField(field, value) { setMethodForm((current) => ({ ...current, [field]: value })); }
  function updateBankAccountField(field, value) { setBankAccountForm((current) => ({ ...current, [field]: value })); }
  function updateWithdrawalField(field, value) { setWithdrawalForm((current) => ({ ...current, [field]: value })); }

  function syncSellerProductState(nextProduct) {
    setSellerProducts((current) => {
      const exists = current.some((item) => item.id === nextProduct.id);
      if (!exists) {
        return [nextProduct, ...current];
      }
      return current.map((item) => (item.id === nextProduct.id ? nextProduct : item));
    });

    setDashboard((current) => {
      const nextProducts = current.content.products.some((item) => item.id === nextProduct.id)
        ? current.content.products.map((item) =>
            item.id === nextProduct.id
              ? {
                  ...item,
                  titleAr: nextProduct.nameAr,
                  titleEn: nextProduct.nameEn,
                  approvalStatus: nextProduct.approvalStatus,
                  isActive: nextProduct.status === "active",
                  submittedAt: nextProduct.submittedAt,
                  approvedAt: nextProduct.approvedAt,
                  rejectionReason: nextProduct.rejectionReason,
                }
              : item,
          )
        : [
            {
              type: "product",
              id: nextProduct.id,
              titleAr: nextProduct.nameAr,
              titleEn: nextProduct.nameEn,
              slug: `product-${nextProduct.id}`,
              approvalStatus: nextProduct.approvalStatus,
              isActive: nextProduct.status === "active",
              submittedAt: nextProduct.submittedAt,
              approvedAt: nextProduct.approvedAt,
              rejectionReason: nextProduct.rejectionReason,
            },
            ...current.content.products,
          ];

      return { ...current, content: { ...current.content, products: nextProducts } };
    });
  }

  function buildManagedContentItem(type, item) {
    return {
      type,
      id: item.id,
      titleAr: item.titleAr || "",
      titleEn: item.titleEn || "",
      slug: item.slug || "",
      approvalStatus: item.approvalStatus || "pending",
      isActive: Boolean(item.isActive ?? true),
      submittedAt: item.submittedAt || "",
      approvedAt: item.approvedAt || "",
      rejectionReason: item.rejectionReason || "",
    };
  }

  function syncManagedContentState(type, nextItem) {
    const stateSetters = {
      services: setProviderServices,
      consultations: setProviderConsultations,
      courses: setProviderCourses,
      jobs: setProviderJobs,
    };

    stateSetters[type]?.((current) => {
      const exists = current.some((item) => item.id === nextItem.id);
      if (!exists) {
        return [nextItem, ...current];
      }
      return current.map((item) => (item.id === nextItem.id ? nextItem : item));
    });

    setDashboard((current) => {
      const nextCollection = current.content[type].some((item) => item.id === nextItem.id)
        ? current.content[type].map((item) =>
            item.id === nextItem.id ? buildManagedContentItem(type.slice(0, -1), nextItem) : item,
          )
        : [buildManagedContentItem(type.slice(0, -1), nextItem), ...current.content[type]];

      return {
        ...current,
        content: {
          ...current.content,
          [type]: nextCollection,
        },
      };
    });
  }

  function removeManagedContentState(type, id) {
    const stateSetters = {
      services: setProviderServices,
      consultations: setProviderConsultations,
      courses: setProviderCourses,
      jobs: setProviderJobs,
    };

    stateSetters[type]?.((current) => current.filter((item) => item.id !== id));
    setDashboard((current) => ({
      ...current,
      content: {
        ...current.content,
        [type]: current.content[type].filter((item) => item.id !== id),
      },
    }));
  }

  // --- Handlers ---
  async function handleSubmit(event) {
    event.preventDefault();
    try {
      setSaving(true);
      const result = await submitProviderApplication({
        provider_role: form.providerRole,
        display_name: form.displayName || null,
        headline: form.headline || null,
        bio: form.bio || null,
        experience_years: form.experienceYears !== "" ? Number(form.experienceYears) : null,
        portfolio_url: form.portfolioUrl || null,
        cv_url: form.cvUrl || null,
        company_name: form.companyName || null,
      });
      setDashboard((current) => {
        const nextProfiles = current.profiles.some((p) => p.id === result.profile.id)
          ? current.profiles.map((p) => (p.id === result.profile.id ? result.profile : p))
          : [result.profile, ...current.profiles];
        return { ...current, profiles: nextProfiles };
      });
      setNotice({ type: "success", message: result.message });
    } catch (error) {
      setNotice({
        type: "danger",
        message: error?.body?.message || error?.message || (isAr ? "فشل إرسال الطلب." : "Failed to submit provider application."),
      });
    } finally {
      setSaving(false);
    }
  }

  function startEditMethod(method) {
    setMethodForm({
      id: method.id,
      method: method.method,
      accountLabel: method.accountLabel || "",
      accountName: method.accountName || "",
      destinationEmail: method.destinationEmail || "",
      destinationReference: method.destinationReference || "",
      isDefault: Boolean(method.isDefault),
    });
  }

  function resetMethodForm() { setMethodForm(createEmptyPayoutMethodForm()); }

  function startEditBankAccount(account) {
    setBankAccountForm({
      id: account.id,
      fullName: account.fullName || "",
      iban: account.iban || "",
      currency: account.currency || "",
      bankName: account.bankName || "",
      swiftCode: account.swiftCode || "",
      addressLine: account.addressLine || "",
      city: account.city || "",
      postalCode: account.postalCode || "",
      isDefault: Boolean(account.isDefault),
      status: account.status || "active",
    });
  }

  function resetBankAccountForm() { setBankAccountForm(createEmptyBankAccountForm()); }

  async function handleSaveMethod(event) {
    event.preventDefault();
    if (savingMethod) return;
    if (!supportedLegacyMethodOptions.some((opt) => opt.code === methodForm.method)) {
      setNotice({
        type: "danger",
        message:
          getCountryPayoutAvailability(earnings, `payouts.legacy_methods.${methodForm.method}`)?.reason ||
          (isAr ? "طريقة السحب هذه غير مدعومة لبلد هذا المستخدم." : "This payout method is not supported for the user country."),
      });
      return;
    }
    try {
      setSavingMethod(true);
      const payload = {
        method: methodForm.method,
        account_label: methodForm.accountLabel || null,
        account_name: methodForm.accountName || null,
        destination_email: methodForm.destinationEmail || null,
        destination_reference: methodForm.destinationReference || null,
        is_default: Boolean(methodForm.isDefault),
      };
      const result = methodForm.id
        ? await updateProviderPayoutMethod(methodForm.id, payload)
        : await createProviderPayoutMethod(payload);
      const refreshedEarnings = await fetchProviderEarnings();
      setEarnings(refreshedEarnings);
      setWithdrawalForm((current) => ({
        ...current,
        methodType: "legacy",
        payoutMethodId: String(result.method.id),
      }));
      resetMethodForm();
      setNotice({ type: "success", message: result.message });
    } catch (error) {
      setNotice({
        type: "danger",
        message: error?.body?.message || error?.message || (isAr ? "فشل حفظ طريقة السحب." : "Failed to save the payout method."),
      });
    } finally {
      setSavingMethod(false);
    }
  }

  async function handleDeleteMethod(method) {
    if (!window.confirm(isAr ? "هل تريد حذف طريقة السحب هذه؟" : "Do you want to delete this payout method?")) return;
    try {
      const result = await deleteProviderPayoutMethod(method.id);
      const refreshedEarnings = await fetchProviderEarnings();
      setEarnings(refreshedEarnings);
      const nextDefaultMethod = refreshedEarnings.methods.find((item) => item.isDefault) || refreshedEarnings.methods[0];
      setNotice({ type: "success", message: result.message });
      if (methodForm.id === method.id) resetMethodForm();
      if (String(method.id) === withdrawalForm.payoutMethodId) {
        setWithdrawalForm((current) => ({
          ...current,
          payoutMethodId: nextDefaultMethod?.id ? String(nextDefaultMethod.id) : "",
        }));
      }
    } catch (error) {
      setNotice({
        type: "danger",
        message: error?.body?.message || error?.message || (isAr ? "فشل حذف طريقة السحب." : "Failed to delete the payout method."),
      });
    }
  }

  async function handleStartStripeLink() {
    try {
      setSavingMethod(true);
      const result = await createStripeConnectLink();
      setNotice({ type: "info", message: result.message });
      if (result.onboardingUrl) { window.location.href = result.onboardingUrl; return; }
      throw new Error(isAr ? "تعذر إنشاء رابط ربط Stripe." : "Unable to create the Stripe onboarding link.");
    } catch (error) {
      setNotice({
        type: "danger",
        message: error?.body?.message || error?.message || (isAr ? "فشل بدء ربط Stripe." : "Failed to start Stripe linking."),
      });
    } finally {
      setSavingMethod(false);
    }
  }

  async function handleOpenStripeDashboard() {
    try {
      setSavingMethod(true);
      const result = await createStripeDashboardLink();
      setNotice({ type: "info", message: result.message });
      if (result.dashboardUrl) { window.location.href = result.dashboardUrl; return; }
      throw new Error(isAr ? "تعذر إنشاء رابط لوحة Stripe." : "Unable to create the Stripe dashboard link.");
    } catch (error) {
      setNotice({
        type: "danger",
        message: error?.body?.message || error?.message || (isAr ? "فشل فتح لوحة Stripe." : "Failed to open the Stripe dashboard."),
      });
    } finally {
      setSavingMethod(false);
    }
  }

  async function handleSaveBankAccount(event) {
    event.preventDefault();
    if (savingBankAccount) return;
    try {
      setSavingBankAccount(true);
      const payload = {
        full_name: bankAccountForm.fullName,
        iban: bankAccountForm.iban,
        currency: bankAccountForm.currency,
        bank_name: bankAccountForm.bankName || null,
        swift_code: bankAccountForm.swiftCode || null,
        address_line: bankAccountForm.addressLine || null,
        city: bankAccountForm.city || null,
        postal_code: bankAccountForm.postalCode || null,
        is_default: Boolean(bankAccountForm.isDefault),
        status: bankAccountForm.status || "active",
      };
      const result = bankAccountForm.id
        ? await updateUserBankAccount(bankAccountForm.id, payload)
        : await createUserBankAccount(payload);
      const refreshedEarnings = await fetchProviderEarnings();
      setEarnings(refreshedEarnings);
      setWithdrawalForm((current) => ({
        ...current,
        bankAccountId: String(result.bankAccount.id),
        methodType: "iban_wise",
      }));
      resetBankAccountForm();
      setNotice({ type: "success", message: result.message });
    } catch (error) {
      setNotice({
        type: "danger",
        message: error?.body?.message || error?.message || (isAr ? "فشل حفظ الحساب البنكي." : "Failed to save the bank account."),
      });
    } finally {
      setSavingBankAccount(false);
    }
  }

  async function handleDeleteBankAccount(account) {
    if (!window.confirm(isAr ? "هل تريد حذف هذا الحساب البنكي؟" : "Do you want to delete this bank account?")) return;
    try {
      const result = await deleteUserBankAccount(account.id);
      const refreshedEarnings = await fetchProviderEarnings();
      setEarnings(refreshedEarnings);
      if (bankAccountForm.id === account.id) resetBankAccountForm();
      if (String(account.id) === withdrawalForm.bankAccountId) {
        setWithdrawalForm((current) => ({ ...current, bankAccountId: "" }));
      }
      setNotice({ type: "success", message: result.message });
    } catch (error) {
      setNotice({
        type: "danger",
        message: error?.body?.message || error?.message || (isAr ? "فشل حذف الحساب البنكي." : "Failed to delete the bank account."),
      });
    }
  }

  async function handleSetDefaultBankAccount(account) {
    try {
      const result = await setDefaultUserBankAccount(account.id);
      const refreshedEarnings = await fetchProviderEarnings();
      setEarnings(refreshedEarnings);
      setNotice({ type: "success", message: result.message });
    } catch (error) {
      setNotice({
        type: "danger",
        message: error?.body?.message || error?.message || (isAr ? "فشل تعيين الحساب الافتراضي." : "Failed to set the default bank account."),
      });
    }
  }

  async function handleCreateWithdrawal(event) {
    event.preventDefault();
    if (savingWithdrawal) return;
    if (withdrawalForm.methodType === "legacy" && !Boolean(legacyWithdrawalAvailability?.supported)) {
      setNotice({
        type: "danger",
        message: legacyWithdrawalAvailability?.reason || (isAr ? "طرق PayPal وStripe غير مدعومة لبلد هذا المستخدم." : "PayPal and Stripe withdrawals are not supported for the user country."),
      });
      return;
    }
    if (withdrawalForm.methodType === "iban_wise" && !Boolean(ibanWithdrawalTypeAvailability?.supported)) {
      setNotice({
        type: "danger",
        message: ibanWithdrawalTypeAvailability?.reason || (isAr ? "السحب عبر Wise IBAN غير مدعوم لبلد هذا المستخدم." : "Wise IBAN withdrawals are not supported for the user country."),
      });
      return;
    }
    try {
      setSavingWithdrawal(true);
      const result =
        withdrawalForm.methodType === "iban_wise"
          ? await createIbanWithdrawalRequest({
              bank_account_id: Number(withdrawalForm.bankAccountId || 0),
              amount: Number(withdrawalForm.amount || 0),
              notes: withdrawalForm.notes || null,
            })
          : await createProviderWithdrawalRequest({
              payout_method_id: Number(withdrawalForm.payoutMethodId || 0),
              amount: Number(withdrawalForm.amount || 0),
              notes: withdrawalForm.notes || null,
            });
      const refreshedEarnings = await fetchProviderEarnings();
      setEarnings(refreshedEarnings);
      setWithdrawalForm((current) => ({ ...current, amount: "", notes: "" }));
      setNotice({ type: "success", message: result.message });
    } catch (error) {
      setNotice({
        type: "danger",
        message: error?.body?.message || error?.message || (isAr ? "فشل إرسال طلب السحب." : "Failed to submit the withdrawal request."),
      });
    } finally {
      setSavingWithdrawal(false);
    }
  }

  function resetServiceForm() { setServiceForm(createEmptyServiceForm()); }
  function resetConsultationForm() { setConsultationForm(createEmptyConsultationForm()); }
  function resetCourseForm() { setCourseForm(createEmptyCourseForm()); }
  function resetJobForm() { setJobForm(createEmptyJobForm()); }
  function resetSellerForm() { setSellerForm(createEmptySellerProductForm()); }
  function resetSellerVariantForm() { setSellerVariantForm(createEmptySellerVariantForm()); }

  function handleEditService(service) {
    setServiceForm({
      id: service.id,
      titleAr: service.titleAr || "",
      titleEn: service.titleEn || "",
      shortDescriptionAr: service.shortDescriptionAr || "",
      shortDescriptionEn: service.shortDescriptionEn || "",
      descriptionAr: service.descriptionAr || "",
      descriptionEn: service.descriptionEn || "",
      price: service.price ? String(service.price) : "",
      currency: service.currency || "USD",
      deliveryDays: service.deliveryDays ? String(service.deliveryDays) : "1",
      revisions: String(service.revisions || 0),
      coverImage: service.coverImage || "",
      isActive: Boolean(service.isActive),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleEditConsultation(consultation) {
    setConsultationForm({
      id: consultation.id,
      titleAr: consultation.titleAr || "",
      titleEn: consultation.titleEn || "",
      shortDescriptionAr: consultation.shortDescriptionAr || "",
      shortDescriptionEn: consultation.shortDescriptionEn || "",
      descriptionAr: consultation.descriptionAr || "",
      descriptionEn: consultation.descriptionEn || "",
      price: consultation.price ? String(consultation.price) : "",
      durationMinutes: consultation.durationMinutes
        ? String(consultation.durationMinutes)
        : "30",
      coverImage: consultation.coverImage || "",
      meetingProvider: consultation.meetingProvider || "",
      category: consultation.category || "",
      isActive: Boolean(consultation.isActive),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleEditCourse(course) {
    setCourseForm({
      id: course.id,
      titleAr: course.titleAr || "",
      titleEn: course.titleEn || "",
      shortDescriptionAr: course.shortDescriptionAr || "",
      shortDescriptionEn: course.shortDescriptionEn || "",
      descriptionAr: course.descriptionAr || "",
      descriptionEn: course.descriptionEn || "",
      thumbnail: course.thumbnail || "",
      promoVideoUrl: course.promoVideoUrl || "",
      price: course.price ? String(course.price) : "",
      salePrice: course.salePrice ? String(course.salePrice) : "",
      isFree: Boolean(course.isFree),
      isActive: Boolean(course.isActive),
      level: course.level || "beginner",
      durationText: course.durationText || "",
      category: course.category || "",
      requirementsAr: course.requirementsAr || "",
      requirementsEn: course.requirementsEn || "",
      whatYouWillLearnAr: course.whatYouWillLearnAr || "",
      whatYouWillLearnEn: course.whatYouWillLearnEn || "",
      sections:
        Array.isArray(course.sections) && course.sections.length > 0
          ? course.sections.map((section) => ({
              id: section.id,
              titleAr: section.titleAr || "",
              titleEn: section.titleEn || "",
              lessons:
                Array.isArray(section.lessons) && section.lessons.length > 0
                  ? section.lessons.map((lesson) => ({
                      id: lesson.id,
                      titleAr: lesson.titleAr || "",
                      titleEn: lesson.titleEn || "",
                      slug: lesson.slug || "",
                      lessonType: lesson.lessonType || "video",
                      videoUrl: lesson.videoUrl || "",
                      contentAr: lesson.contentAr || "",
                      contentEn: lesson.contentEn || "",
                      attachmentPath: lesson.attachmentPath || "",
                      durationSeconds:
                        lesson.durationSeconds !== null && lesson.durationSeconds !== undefined
                          ? String(lesson.durationSeconds)
                          : "",
                      isPreview: Boolean(lesson.isPreview),
                    }))
                  : createEmptyCourseForm().sections[0].lessons,
            }))
          : createEmptyCourseForm().sections,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleEditJob(job) {
    setJobForm({
      id: job.id,
      titleAr: job.titleAr || "",
      titleEn: job.titleEn || "",
      shortDescriptionAr: job.shortDescriptionAr || "",
      shortDescriptionEn: job.shortDescriptionEn || "",
      descriptionAr: job.descriptionAr || "",
      descriptionEn: job.descriptionEn || "",
      company: job.company || "",
      salaryMin: job.salaryMin !== null && job.salaryMin !== undefined ? String(job.salaryMin) : "",
      salaryMax: job.salaryMax !== null && job.salaryMax !== undefined ? String(job.salaryMax) : "",
      salaryText: job.salaryText || "",
      employmentType: job.employmentType || "full-time",
      location: job.location || "",
      experienceLevel: job.experienceLevel || "",
      coverImage: job.coverImage || "",
      applicationDeadline: job.applicationDeadline ? String(job.applicationDeadline).slice(0, 10) : "",
      isActive: Boolean(job.isActive),
      isFeatured: Boolean(job.isFeatured),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleSaveService(event) {
    event.preventDefault();
    try {
      setSavingService(true);
      const payload = {
        title_ar: serviceForm.titleAr || null,
        title_en: serviceForm.titleEn || null,
        short_description_ar: serviceForm.shortDescriptionAr || null,
        short_description_en: serviceForm.shortDescriptionEn || null,
        description_ar: serviceForm.descriptionAr || null,
        description_en: serviceForm.descriptionEn || null,
        price: Number(serviceForm.price || 0),
        currency: serviceForm.currency || "USD",
        delivery_days: Number(serviceForm.deliveryDays || 1),
        revisions: Number(serviceForm.revisions || 0),
        cover_image: serviceForm.coverImage || null,
        is_active: Boolean(serviceForm.isActive),
      };
      const result = serviceForm.id
        ? await updateProviderService(serviceForm.id, payload)
        : await createProviderService(payload);
      syncManagedContentState("services", result.service);
      resetServiceForm();
      setNotice({ type: "success", message: result.message });
    } catch (error) {
      setNotice({
        type: "danger",
        message:
          error?.body?.message ||
          error?.message ||
          (isAr ? "فشل حفظ الخدمة." : "Failed to save the service."),
      });
    } finally {
      setSavingService(false);
    }
  }

  async function handleSaveConsultation(event) {
    event.preventDefault();
    try {
      setSavingConsultation(true);
      const payload = {
        title_ar: consultationForm.titleAr || null,
        title_en: consultationForm.titleEn || null,
        short_description_ar: consultationForm.shortDescriptionAr || null,
        short_description_en: consultationForm.shortDescriptionEn || null,
        description_ar: consultationForm.descriptionAr || null,
        description_en: consultationForm.descriptionEn || null,
        price: Number(consultationForm.price || 0),
        duration_minutes: Number(consultationForm.durationMinutes || 30),
        cover_image: consultationForm.coverImage || null,
        meeting_provider: consultationForm.meetingProvider || null,
        category: consultationForm.category || null,
        is_active: Boolean(consultationForm.isActive),
      };
      const result = consultationForm.id
        ? await updateProviderConsultation(consultationForm.id, payload)
        : await createProviderConsultation(payload);
      syncManagedContentState("consultations", result.consultation);
      resetConsultationForm();
      setNotice({ type: "success", message: result.message });
    } catch (error) {
      setNotice({
        type: "danger",
        message:
          error?.body?.message ||
          error?.message ||
          (isAr ? "فشل حفظ الاستشارة." : "Failed to save the consultation."),
      });
    } finally {
      setSavingConsultation(false);
    }
  }

  async function handleSaveCourse(event) {
    event.preventDefault();
    try {
      setSavingCourse(true);
      const payload = {
        title_ar: courseForm.titleAr || null,
        title_en: courseForm.titleEn || null,
        short_description_ar: courseForm.shortDescriptionAr || null,
        short_description_en: courseForm.shortDescriptionEn || null,
        description_ar: courseForm.descriptionAr || null,
        description_en: courseForm.descriptionEn || null,
        thumbnail: courseForm.thumbnail || null,
        promo_video_url: courseForm.promoVideoUrl || null,
        price: courseForm.isFree ? 0 : Number(courseForm.price || 0),
        sale_price:
          !courseForm.isFree && courseForm.salePrice !== ""
            ? Number(courseForm.salePrice || 0)
            : null,
        is_free: Boolean(courseForm.isFree),
        is_active: Boolean(courseForm.isActive),
        level: courseForm.level || "beginner",
        duration_text: courseForm.durationText || null,
        category: courseForm.category || null,
        requirements_ar: courseForm.requirementsAr || null,
        requirements_en: courseForm.requirementsEn || null,
        what_you_will_learn_ar: courseForm.whatYouWillLearnAr || null,
        what_you_will_learn_en: courseForm.whatYouWillLearnEn || null,
        sections: Array.isArray(courseForm.sections)
          ? courseForm.sections.map((section, sectionIndex) => ({
              ...(section.id ? { id: section.id } : {}),
              title_ar: section.titleAr || "",
              title_en: section.titleEn || "",
              sort_order: sectionIndex,
              lessons: Array.isArray(section.lessons)
                ? section.lessons.map((lesson, lessonIndex) => ({
                    ...(lesson.id ? { id: lesson.id } : {}),
                    title_ar: lesson.titleAr || "",
                    title_en: lesson.titleEn || "",
                    slug: lesson.slug || null,
                    lesson_type: lesson.lessonType || "video",
                    video_url: lesson.videoUrl || null,
                    content_ar: lesson.contentAr || null,
                    content_en: lesson.contentEn || null,
                    attachment_path: lesson.attachmentPath || null,
                    duration_seconds:
                      lesson.durationSeconds !== "" ? Number(lesson.durationSeconds || 0) : null,
                    is_preview: Boolean(lesson.isPreview),
                    sort_order: lessonIndex,
                  }))
                : [],
            }))
          : [],
      };
      const result = courseForm.id
        ? await updateProviderCourse(courseForm.id, payload)
        : await createProviderCourse(payload);
      syncManagedContentState("courses", result.course);
      resetCourseForm();
      setNotice({ type: "success", message: result.message });
    } catch (error) {
      setNotice({
        type: "danger",
        message:
          error?.body?.message ||
          error?.message ||
          (isAr ? "فشل حفظ الكورس." : "Failed to save the course."),
      });
    } finally {
      setSavingCourse(false);
    }
  }

  async function handleSaveJob(event) {
    event.preventDefault();
    try {
      setSavingJob(true);
      const payload = {
        title_ar: jobForm.titleAr || null,
        title_en: jobForm.titleEn || null,
        short_description_ar: jobForm.shortDescriptionAr || null,
        short_description_en: jobForm.shortDescriptionEn || null,
        description_ar: jobForm.descriptionAr || null,
        description_en: jobForm.descriptionEn || null,
        company: jobForm.company || null,
        salary_min: jobForm.salaryMin !== "" ? Number(jobForm.salaryMin || 0) : null,
        salary_max: jobForm.salaryMax !== "" ? Number(jobForm.salaryMax || 0) : null,
        salary_text: jobForm.salaryText || null,
        employment_type: jobForm.employmentType || "full-time",
        location: jobForm.location || null,
        experience_level: jobForm.experienceLevel || null,
        cover_image: jobForm.coverImage || null,
        application_deadline: jobForm.applicationDeadline || null,
        is_active: Boolean(jobForm.isActive),
        is_featured: Boolean(jobForm.isFeatured),
      };
      const result = jobForm.id
        ? await updateProviderJob(jobForm.id, payload)
        : await createProviderJob(payload);
      syncManagedContentState("jobs", result.job);
      resetJobForm();
      setNotice({ type: "success", message: result.message });
    } catch (error) {
      setNotice({
        type: "danger",
        message:
          error?.body?.message ||
          error?.message ||
          (isAr ? "فشل حفظ الوظيفة." : "Failed to save the job."),
      });
    } finally {
      setSavingJob(false);
    }
  }

  async function handleDeleteService(service) {
    if (!window.confirm(isAr ? "هل تريد حذف هذه الخدمة؟" : "Do you want to delete this service?")) {
      return;
    }
    try {
      const result = await deleteProviderService(service.id);
      removeManagedContentState("services", service.id);
      if (serviceForm.id === service.id) resetServiceForm();
      setNotice({ type: "success", message: result.message });
    } catch (error) {
      setNotice({
        type: "danger",
        message:
          error?.body?.message ||
          error?.message ||
          (isAr ? "فشل حذف الخدمة." : "Failed to delete the service."),
      });
    }
  }

  async function handleDeleteConsultation(consultation) {
    if (!window.confirm(isAr ? "هل تريد حذف هذه الاستشارة؟" : "Do you want to delete this consultation?")) {
      return;
    }
    try {
      const result = await deleteProviderConsultation(consultation.id);
      removeManagedContentState("consultations", consultation.id);
      if (consultationForm.id === consultation.id) resetConsultationForm();
      setNotice({ type: "success", message: result.message });
    } catch (error) {
      setNotice({
        type: "danger",
        message:
          error?.body?.message ||
          error?.message ||
          (isAr ? "فشل حذف الاستشارة." : "Failed to delete the consultation."),
      });
    }
  }

  async function handleDeleteCourse(course) {
    if (!window.confirm(isAr ? "هل تريد حذف هذا الكورس؟" : "Do you want to delete this course?")) {
      return;
    }
    try {
      const result = await deleteProviderCourse(course.id);
      removeManagedContentState("courses", course.id);
      if (courseForm.id === course.id) resetCourseForm();
      setNotice({ type: "success", message: result.message });
    } catch (error) {
      setNotice({
        type: "danger",
        message:
          error?.body?.message ||
          error?.message ||
          (isAr ? "فشل حذف الكورس." : "Failed to delete the course."),
      });
    }
  }

  async function handleDeleteJob(job) {
    if (!window.confirm(isAr ? "هل تريد حذف هذه الوظيفة؟" : "Do you want to delete this job?")) {
      return;
    }
    try {
      const result = await deleteProviderJob(job.id);
      removeManagedContentState("jobs", job.id);
      if (jobForm.id === job.id) resetJobForm();
      setNotice({ type: "success", message: result.message });
    } catch (error) {
      setNotice({
        type: "danger",
        message:
          error?.body?.message ||
          error?.message ||
          (isAr ? "فشل حذف الوظيفة." : "Failed to delete the job."),
      });
    }
  }

  function handleEditSellerProduct(product) {
    setSellerForm({
      id: product.id,
      nameAr: product.nameAr || "",
      nameEn: product.nameEn || "",
      descriptionAr: product.descriptionAr || "",
      descriptionEn: product.descriptionEn || "",
      categoryId: product.categoryId ? String(product.categoryId) : "",
      price: product.price ? String(product.price) : "",
      salePrice: product.salePrice ? String(product.salePrice) : "",
      color: product.color || "",
      image: product.rawImage || product.image || "",
      imageFile: null,
      imagePreview: product.image || "",
      stock:
        product.isDigital || !Number.isFinite(product.stock) || Number(product.stock) >= 999999
          ? ""
          : String(product.stock || 0),
      isDigital: Boolean(product.isDigital),
      digitalFilePath: product.digitalFilePath || "",
      digitalFile: null,
      status: product.status || "active",
    });
    setEditingSellerVariantsProductId(product.id);
    resetSellerVariantForm();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleManageSellerVariants(product) {
    setEditingSellerVariantsProductId(product.id);
    resetSellerVariantForm();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleEditSellerVariant(product, variant) {
    setEditingSellerVariantsProductId(product.id);
    setSellerVariantForm({
      id: variant.id,
      attributeId: variant.attributeId ? String(variant.attributeId) : "",
      valueId: variant.valueId ? String(variant.valueId) : "",
      colorId: variant.colorId ? String(variant.colorId) : "",
      sizeId: variant.sizeId ? String(variant.sizeId) : "",
      sku: variant.sku || "",
      price: variant.price ? String(variant.price) : "",
      salePrice: variant.salePrice ? String(variant.salePrice) : "",
      stock: String(variant.stock || 0),
      lowStockAlert: String(variant.lowStockAlert || 5),
      image: variant.rawImage || variant.image || "",
      imageFile: null,
      imagePreview: variant.image || "",
    });
  }

  async function handleSaveSellerProduct(event) {
    event.preventDefault();
    try {
      setSavingSellerProduct(true);
      const payload = {
        name_ar: sellerForm.nameAr || null,
        name_en: sellerForm.nameEn || null,
        description_ar: sellerForm.descriptionAr || null,
        description_en: sellerForm.descriptionEn || null,
        category_id: Number(sellerForm.categoryId || 0),
        price: Number(sellerForm.price || 0),
        sale_price: sellerForm.salePrice !== "" ? Number(sellerForm.salePrice || 0) : null,
        color: sellerForm.color || null,
        image: sellerForm.image || null,
        image_file: sellerForm.imageFile || null,
        stock: sellerForm.isDigital ? null : Number(sellerForm.stock || 0),
        is_digital: Boolean(sellerForm.isDigital),
        digital_file_path: sellerForm.isDigital ? sellerForm.digitalFilePath || null : null,
        digital_file: sellerForm.isDigital ? sellerForm.digitalFile || null : null,
        status: sellerForm.status || "active",
      };
      const result = sellerForm.id
        ? await updateSellerProduct(sellerForm.id, payload)
        : await createSellerProduct(payload);
      syncSellerProductState(result.product);
      setEditingSellerVariantsProductId(result.product.id);
      resetSellerForm();
      resetSellerVariantForm();
      setNotice({ type: "success", message: result.message });
    } catch (error) {
      setNotice({
        type: "danger",
        message: error?.body?.message || error?.message || (isAr ? "فشل حفظ المنتج." : "Failed to save the product."),
      });
    } finally {
      setSavingSellerProduct(false);
    }
  }

  async function handleDeleteSellerProduct(product) {
    if (!window.confirm(isAr ? "هل تريد حذف هذا المنتج؟" : "Do you want to delete this product?")) return;
    try {
      const result = await deleteSellerProduct(product.id);
      setSellerProducts((current) => current.filter((item) => item.id !== product.id));
      setDashboard((current) => ({
        ...current,
        content: {
          ...current.content,
          products: current.content.products.filter((item) => item.id !== product.id),
        },
      }));
      if (sellerForm.id === product.id) resetSellerForm();
      if (editingSellerVariantsProductId === product.id) {
        setEditingSellerVariantsProductId(0);
        resetSellerVariantForm();
      }
      setNotice({ type: "success", message: result.message });
    } catch (error) {
      setNotice({
        type: "danger",
        message: error?.body?.message || error?.message || (isAr ? "فشل حذف المنتج." : "Failed to delete the product."),
      });
    }
  }

  async function handleSaveSellerVariant(event) {
    event.preventDefault();

    if (!activeSellerVariantProduct) {
      setNotice({
        type: "danger",
        message: isAr ? "اختر منتجًا لإدارة المتغيرات أولًا." : "Choose a product before managing variants.",
      });
      return;
    }

    try {
      setSavingSellerVariant(true);
      const payload = {
        value_id: sellerVariantForm.valueId ? Number(sellerVariantForm.valueId) : null,
        color_id: sellerVariantForm.colorId ? Number(sellerVariantForm.colorId) : null,
        size_id: sellerVariantForm.sizeId ? Number(sellerVariantForm.sizeId) : null,
        sku: sellerVariantForm.sku || null,
        price: Number(sellerVariantForm.price || 0),
        sale_price:
          sellerVariantForm.salePrice !== "" ? Number(sellerVariantForm.salePrice || 0) : null,
        stock: Number(sellerVariantForm.stock || 0),
        low_stock_alert: Number(sellerVariantForm.lowStockAlert || 0),
        image: sellerVariantForm.image || null,
        image_file: sellerVariantForm.imageFile || null,
      };

      const result = sellerVariantForm.id
        ? await updateSellerProductVariant(
            activeSellerVariantProduct.id,
            sellerVariantForm.id,
            payload,
          )
        : await createSellerProductVariant(activeSellerVariantProduct.id, payload);

      syncSellerProductState(result.product);
      resetSellerVariantForm();
      setNotice({ type: "success", message: result.message });
    } catch (error) {
      setNotice({
        type: "danger",
        message:
          error?.body?.message ||
          error?.message ||
          (isAr ? "فشل حفظ المتغير." : "Failed to save the variant."),
      });
    } finally {
      setSavingSellerVariant(false);
    }
  }

  async function handleDeleteSellerVariant(product, variant) {
    if (!window.confirm(isAr ? "هل تريد حذف هذا المتغير؟" : "Do you want to delete this variant?")) {
      return;
    }

    try {
      const result = await deleteSellerProductVariant(product.id, variant.id);
      syncSellerProductState(result.product);
      if (sellerVariantForm.id === variant.id) {
        resetSellerVariantForm();
      }
      setNotice({ type: "success", message: result.message });
    } catch (error) {
      setNotice({
        type: "danger",
        message:
          error?.body?.message ||
          error?.message ||
          (isAr ? "فشل حذف المتغير." : "Failed to delete the variant."),
      });
    }
  }

  // --- Render ---
  return (
    <div
      className={[
        embedded ? "provider-center provider-center--dashboard" : "provider-center container py-4",
      ].join(" ")}
    >
      {!embedded ? (
        <div className="mb-4">
          <h2 className="mb-1">{isAr ? "مركز مقدم الخدمة" : "Provider Center"}</h2>
          <p className="text-muted mb-0">
            {isAr
              ? "تابع حالة اعتمادك، وأدر أرباحك وطرق السحب، وقدم منتجاتك كبائع بعد موافقة الإدارة."
              : "Track your approval status, manage earnings and withdrawals, and submit products as a seller after admin approval."}
          </p>
        </div>
      ) : null}

      {notice ? (
        <div className={`alert alert-${notice.type === "success" ? "success" : "danger"} provider-center__notice`}>
          {notice.message}
        </div>
      ) : null}

      {!loading && !embedded ? (
        <div className="card border-0 shadow-sm mb-4 overflow-hidden provider-center__hero">
          <div className="card-body p-4 p-lg-5">
            <div className="row g-4 align-items-center">
              <div className="col-12 col-xl-7">
                <div className="provider-center__eyebrow">{isAr ? "لوحة الأعمال" : "Business Dashboard"}</div>
                <h3 className="provider-center__hero-title fw-bold mb-2">
                  {isAr
                    ? "تحكم من مكان واحد في البائع والخدمات والاستشارات والكورسات"
                    : "Manage seller, services, consultations, and courses from one place"}
                </h3>
                <p className="provider-center__hero-copy mb-0">
                  {isAr
                    ? "هذه اللوحة تجمع حالة الاعتماد، المحتوى المرسل، الأرباح، وطرق السحب مع اختصارات مباشرة إلى كل قسم."
                    : "This workspace brings together approval status, submitted content, earnings, and payout methods with direct shortcuts to each area."}
                </p>
              </div>

              <div className="col-12 col-xl-5">
                <div className="row g-3">
                  {dashboardHighlights.map((item) => (
                    <div className="col-6" key={item.key}>
                      <div className="provider-center__highlight h-100">
                        <div className="provider-center__highlight-icon">
                          <i className={`bi ${item.icon}`} />
                        </div>
                        <div className="small text-muted">{item.label}</div>
                        <div className={`fw-bold provider-center__highlight-value ${item.emphasisClassName || ""}`}>
                          {item.value}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="provider-center__quick-actions mt-4">
              {dashboardQuickActions.map((action) => (
                <a
                  key={action.key}
                  href={action.href}
                  className={`rounded-pill ${action.dark ? "btn btn-dark" : "btn btn-outline-dark"} provider-center__quick-action`}
                >
                  {action.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {!loading && embedded && (
        <div className="provider-center__activities-grid mb-4">
          {dashboardSections.map((section) => (
            <ProviderActivityCard key={section.key} {...section} isAr={isAr} />
          ))}
        </div>
      )}

      {loading ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body text-muted">
            {isAr ? "جارٍ تحميل مركز مقدم الخدمة..." : "Loading provider center..."}
          </div>
        </div>
      ) : (
        <>
          {activeTab !== "overview" && (
            <div className="provider-center__workspace-stack">
              {(!embedded || activeTab === "application") && (
          <WorkspaceSection
            id={getDashboardSectionId("application")}
            eyebrow={isAr ? "الخطوة الأولى" : "Step One"}
            title={isAr ? "الاعتماد والأهلية" : "Applications & eligibility"}
            description={
              isAr
                ? "ابدأ بتفعيل دور مقدم الخدمة المناسب لك. من هنا تُرسل الطلبات وتتابع حالة كل اعتماد وملاحظات الإدارة."
                : "Start by activating the provider role that matches your work. This area handles new applications, status tracking, and admin feedback."
            }
            stats={[
              { label: isAr ? "الأدوار المرسلة" : "Submitted roles", value: dashboard.profiles.length },
              {
                label: isAr ? "المعتمد" : "Approved",
                value: dashboard.profiles.filter((item) => item.status === "approved").length,
              },
              {
                label: isAr ? "قيد المراجعة" : "Under review",
                value: dashboard.profiles.filter((item) => item.status === "pending" || item.status === "review").length,
              },
            ]}
          >
            <div className="row g-4">
              <div className="col-12 col-xl-7">
                <ApplicationPanel
                  form={form}
                  updateField={updateField}
                  visibleProviderRoles={visibleProviderRoles}
                  isCurrentRoleEnabled={isCurrentRoleEnabled}
                  disabledRoleMessages={disabledRoleMessages}
                  currentRoleProfile={currentRoleProfile}
                  saving={saving}
                  handleSubmit={handleSubmit}
                  isAr={isAr}
                />
              </div>
              <div className="col-12 col-xl-5">
                <div className="provider-center__info-strip h-100">
                  <h5 className="mb-3">{isAr ? "ما الذي يفتح بعد الاعتماد؟" : "What unlocks after approval?"}</h5>
                  <div className="provider-center__info-points">
                    <div>{isAr ? "إضافة المحتوى أو المنتجات بحسب الدور الذي تم اعتماده." : "Create products or content based on the role you were approved for."}</div>
                    <div>{isAr ? "ربط وسائل السحب والحسابات البنكية." : "Connect payout methods and bank accounts."}</div>
                    <div>{isAr ? "استلام الأرباح ومتابعة المراجعات من نفس اللوحة." : "Receive earnings and track reviews from the same workspace."}</div>
                  </div>
                </div>
              </div>
            </div>
          </WorkspaceSection>
          )}

          {(!embedded || activeTab === "payouts") && (
          <WorkspaceSection
            id={getDashboardSectionId("payouts")}
            eyebrow={isAr ? "العمليات المالية" : "Money flow"}
            title={isAr ? "الأرباح والسحب" : "Earnings & payouts"}
            description={
              isAr
                ? "هذا القسم هو مركزك المالي الكامل: الرصيد، ربط وسائل السحب، الحسابات البنكية، وطلبات السحب السابقة والجديدة."
                : "This is your full finance workspace: balances, payout connections, bank accounts, and past or new withdrawal requests."
            }
            stats={[
              { label: isAr ? "المتاح الآن" : "Available now", value: formatMoney(earnings.summary.available_balance, earnings.summary.currency) },
              { label: isAr ? "إجمالي الأرباح" : "Total earned", value: formatMoney(earnings.summary.total_earned, earnings.summary.currency) },
              { label: isAr ? "طلبات السحب" : "Withdrawal requests", value: earnings.requests.length },
            ]}
            actions={[
              { href: "#withdrawal-panel", label: isAr ? "طلب سحب جديد" : "New withdrawal", dark: true },
              { href: "#payout-methods-panel", label: isAr ? "ربط وسائل السحب" : "Connect payout methods" },
            ]}
          >
            <div className="row g-4" style={{ alignItems: 'flex-start' }}>
              <div className="col-12 col-xl-5">
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: '24px' }}>
                  <PayoutMethodsPanel
                    earnings={earnings}
                    stripeLinkedMethod={stripeLinkedMethod}
                    paypalLinkedMethod={paypalLinkedMethod}
                    testPayPalLinkedMethod={testPayPalLinkedMethod}
                    payPalLinkConfig={payPalLinkConfig}
                    testPayPalLinkConfig={testPayPalLinkConfig}
                    payPalButtonStates={payPalButtonStates}
                    savingMethod={savingMethod}
                    handleStartStripeLink={handleStartStripeLink}
                    handleOpenStripeDashboard={handleOpenStripeDashboard}
                    onRetryPayPal={() => {
                      payPalPayoutScriptPromise = null;
                      setPayPalButtonStates((current) => ({ ...current, paypal: "idle" }));
                      setPayPalButtonRetryKey((current) => current + 1);
                    }}
                    onRetryTestPayPal={() => {
                      payPalPayoutScriptPromise = null;
                      setPayPalButtonStates((current) => ({ ...current, test_paypal: "idle" }));
                      setPayPalButtonRetryKey((current) => current + 1);
                    }}
                    isAr={isAr}
                  />
                  <BankAccountsPanel
                    earnings={earnings}
                    bankAccountForm={bankAccountForm}
                    updateBankAccountField={updateBankAccountField}
                    resetBankAccountForm={resetBankAccountForm}
                    enteredBankCountryCode={enteredBankCountryCode}
                    savingBankAccount={savingBankAccount}
                    handleSaveBankAccount={handleSaveBankAccount}
                    handleDeleteBankAccount={handleDeleteBankAccount}
                    handleSetDefaultBankAccount={handleSetDefaultBankAccount}
                    startEditBankAccount={startEditBankAccount}
                    isAr={isAr}
                  />
                </div>
              </div>
              <div className="col-12 col-xl-7">
                <EarningsSummary earnings={earnings} isAr={isAr} />
                <WithdrawalPanel
                  earnings={earnings}
                  withdrawalForm={withdrawalForm}
                  updateWithdrawalField={updateWithdrawalField}
                  savingWithdrawal={savingWithdrawal}
                  handleCreateWithdrawal={handleCreateWithdrawal}
                  legacyWithdrawalAvailability={legacyWithdrawalAvailability}
                  ibanWithdrawalTypeAvailability={ibanWithdrawalTypeAvailability}
                  ibanWithdrawalEnabled={ibanWithdrawalEnabled}
                  supportedLegacyMethodOptions={supportedLegacyMethodOptions}
                  activeLegacyMethods={activeLegacyMethods}
                  activeLegacyMethodLabels={activeLegacyMethodLabels}
                  ibanPlatformFee={ibanPlatformFee}
                  ibanNetAmount={ibanNetAmount}
                  selectedBankAccount={selectedBankAccount}
                  isAr={isAr}
                />
              </div>
              <div className="col-12">
                <div className="row g-4">
                  <div className="col-12 col-xl-6">
                    <SavedMethodsList
                      earnings={earnings}
                      startEditMethod={startEditMethod}
                      handleDeleteMethod={handleDeleteMethod}
                      isAr={isAr}
                    />
                  </div>
                </div>
              </div>
              <div className="col-12">
                <WithdrawalHistory earnings={earnings} isAr={isAr} isMobile={isMobile} />
              </div>
            </div>
          </WorkspaceSection>
          )}

          {(!embedded || activeTab === "seller") && (
          <WorkspaceSection
            id={getDashboardSectionId("seller")}
            eyebrow={isAr ? "المتجر" : "Storefront"}
            title={isAr ? "منطقة منتجات البائع" : "Seller products workspace"}
            description={
              isAr
                ? "أضف منتجاتك، عدّلها، وراقب حالتها في المراجعة والنشر من نفس المكان."
                : "Create, edit, and monitor your store products and their review status from one section."
            }
            stats={[
              { label: isAr ? "المنتجات الحالية" : "Current products", value: sellerProducts.length },
              { label: isAr ? "قيد المراجعة" : "Pending review", value: sellerProducts.filter((item) => item.approvalStatus === "pending").length },
              { label: isAr ? "المنشور" : "Published", value: sellerProducts.filter((item) => item.status === "active").length },
            ]}
          >
            <SellerProductsPanel
              sellerProducts={sellerProducts}
              sellerMeta={sellerMeta}
              sellerMarketplaceEnabled={sellerMarketplaceEnabled}
              sellerProfile={sellerProfile}
              sellerForm={sellerForm}
              sellerVariantForm={sellerVariantForm}
              activeSellerVariantProduct={activeSellerVariantProduct}
              sellerOptionAttributes={sellerOptionAttributes}
              updateSellerField={updateSellerField}
              updateSellerVariantField={updateSellerVariantField}
              setSellerForm={setSellerForm}
              setSellerVariantForm={setSellerVariantForm}
              resetSellerForm={resetSellerForm}
              resetSellerVariantForm={resetSellerVariantForm}
              savingSellerProduct={savingSellerProduct}
              savingSellerVariant={savingSellerVariant}
              handleSaveSellerProduct={handleSaveSellerProduct}
              handleSaveSellerVariant={handleSaveSellerVariant}
              handleDeleteSellerProduct={handleDeleteSellerProduct}
              handleDeleteSellerVariant={handleDeleteSellerVariant}
              handleEditSellerProduct={handleEditSellerProduct}
              handleEditSellerVariant={handleEditSellerVariant}
              handleManageSellerVariants={handleManageSellerVariants}
              currency={earnings.summary.currency}
              isAdmin={isAdmin}
              isAr={isAr}
              isMobile={isMobile}
            />
            <ContentStatusTable
              title={isAr ? "حالة المنتجات المرسلة" : "Submitted product status"}
              items={dashboard.content.products}
              isAr={isAr}
              isMobile={isMobile}
            />
          </WorkspaceSection>
          )}

          {(!embedded || activeTab === "services") && (
          <WorkspaceSection
            id={getDashboardSectionId("services")}
            eyebrow={isAr ? "الخدمات" : "Services"}
            title={isAr ? "إدارة الخدمات والطلبات" : "Services & orders"}
            description={
              isAr
                ? "راقب الخدمات التي أرسلتها للمراجعة، وافتح صفحات الطلبات الحالية بسرعة."
                : "Track submitted services and jump directly to your active service-order pages."
            }
            stats={[
              { label: isAr ? "الخدمات المرسلة" : "Submitted services", value: dashboard.content.services.length },
              { label: isAr ? "قيد المراجعة" : "Pending review", value: getContentPendingCount(dashboard.content.services) },
            ]}
            actions={[
              { to: "/my-freelancer-orders", label: isAr ? "طلباتي كمقدم خدمة" : "My freelancer orders", dark: true },
              { to: "/my-service-orders", label: isAr ? "طلبات العملاء" : "Client service orders" },
            ]}
          >
            <ServicesManagerPanel
              services={providerServices}
              servicesEnabled={effectiveSections.services}
              freelancerProfile={freelancerProfile}
              onGoToApplication={() => setActiveTab("application")}
              serviceForm={serviceForm}
              updateServiceField={updateServiceField}
              resetServiceForm={resetServiceForm}
              savingService={savingService}
              handleSaveService={handleSaveService}
              handleEditService={handleEditService}
              handleDeleteService={handleDeleteService}
              isAdmin={isAdmin}
              isAr={isAr}
              isMobile={isMobile}
            />
            <ContentStatusTable
              title={isAr ? "الخدمات التي أرسلتها" : "Submitted services"}
              items={dashboard.content.services}
              isAr={isAr}
              isMobile={isMobile}
            />
          </WorkspaceSection>
          )}

          {(!embedded || activeTab === "consultations") && (
          <WorkspaceSection
            id={getDashboardSectionId("consultations")}
            eyebrow={isAr ? "الاستشارات" : "Consultations"}
            title={isAr ? "جلسات الاستشارة والحجوزات" : "Consultation sessions & bookings"}
            description={
              isAr
                ? "تابع الاستشارات المرسلة وحالة اعتمادها، وافتح حجوزاتك الحالية من هنا."
                : "Track submitted consultations, their approval flow, and your active bookings from here."
            }
            stats={[
              { label: isAr ? "الاستشارات المرسلة" : "Submitted consultations", value: dashboard.content.consultations.length },
              { label: isAr ? "قيد المراجعة" : "Pending review", value: getContentPendingCount(dashboard.content.consultations) },
            ]}
            actions={[
              { to: "/my-consultations", label: isAr ? "فتح الاستشارات" : "Open consultations", dark: true },
            ]}
          >
            <ConsultationsManagerPanel
              consultations={providerConsultations}
              consultationsEnabled={effectiveSections.consultations}
              consultantProfile={consultantProfile}
              consultationForm={consultationForm}
              updateConsultationField={updateConsultationField}
              resetConsultationForm={resetConsultationForm}
              savingConsultation={savingConsultation}
              handleSaveConsultation={handleSaveConsultation}
              handleEditConsultation={handleEditConsultation}
              handleDeleteConsultation={handleDeleteConsultation}
              isAdmin={isAdmin}
              isAr={isAr}
              isMobile={isMobile}
            />
            <ContentStatusTable
              title={isAr ? "الاستشارات التي أرسلتها" : "Submitted consultations"}
              items={dashboard.content.consultations}
              isAr={isAr}
              isMobile={isMobile}
            />
          </WorkspaceSection>
          )}

          {(!embedded || activeTab === "courses") && (
          <WorkspaceSection
            id={getDashboardSectionId("courses")}
            eyebrow={isAr ? "التدريب" : "Training"}
            title={isAr ? "الكورسات والمحتوى التعليمي" : "Courses & learning content"}
            description={
              isAr
                ? "اعرض الكورسات المرسلة، وراقب حالتها، ثم انتقل إلى صفحة الكورسات الخاصة بك عند الحاجة."
                : "Review submitted courses, monitor approval status, and jump to your course pages when needed."
            }
            stats={[
              { label: isAr ? "الكورسات المرسلة" : "Submitted courses", value: dashboard.content.courses.length },
              { label: isAr ? "قيد المراجعة" : "Pending review", value: getContentPendingCount(dashboard.content.courses) },
            ]}
            actions={[
              { to: "/my-courses", label: isAr ? "فتح كورساتي" : "Open my courses", dark: true },
            ]}
          >
            <CoursesManagerPanel
              courses={providerCourses}
              coursesEnabled={effectiveSections.courses}
              trainerProfile={trainerProfile}
              courseForm={courseForm}
              updateCourseField={updateCourseField}
              setCourseForm={setCourseForm}
              resetCourseForm={resetCourseForm}
              savingCourse={savingCourse}
              handleSaveCourse={handleSaveCourse}
              handleEditCourse={handleEditCourse}
              handleDeleteCourse={handleDeleteCourse}
              isAdmin={isAdmin}
              isAr={isAr}
              isMobile={isMobile}
            />
            <ContentStatusTable
              title={isAr ? "الكورسات التي أرسلتها" : "Submitted courses"}
              items={dashboard.content.courses}
              isAr={isAr}
              isMobile={isMobile}
            />
          </WorkspaceSection>

          )}

          {(!embedded || activeTab === "jobs") && effectiveSections.jobs ? (
            <WorkspaceSection
              id={getDashboardSectionId("jobs")}
              eyebrow={isAr ? "التوظيف" : "Hiring"}
              title={isAr ? "إعلانات الوظائف" : "Job listings"}
              description={
                isAr
                  ? "أدر الوظائف التي أرسلتها للنشر وراجع حالتها من هذه المساحة."
                  : "Manage the job listings you submitted and track their publishing status from this workspace."
              }
              stats={[
                { label: isAr ? "الوظائف المرسلة" : "Submitted jobs", value: dashboard.content.jobs.length },
                { label: isAr ? "قيد المراجعة" : "Pending review", value: getContentPendingCount(dashboard.content.jobs) },
              ]}
            >
              <JobsManagerPanel
                jobs={providerJobs}
                jobsEnabled={effectiveSections.jobs}
                isAdmin={isAdmin}
                jobForm={jobForm}
                updateJobField={updateJobField}
                savingJob={savingJob}
                resetJobForm={resetJobForm}
                handleSaveJob={handleSaveJob}
                handleEditJob={handleEditJob}
                handleDeleteJob={handleDeleteJob}
                isAr={isAr}
                isMobile={isMobile}
              />
              <ContentStatusTable
                title={isAr ? "الوظائف التي أرسلتها" : "Submitted jobs"}
                items={dashboard.content.jobs}
                isAr={isAr}
                isMobile={isMobile}
              />
            </WorkspaceSection>
          ) : null}
            </div>
          )}
        </>
      )}
    </div>
  );
}
